import { css, html, LitElement, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { type CardOption } from "../../../tools/ask-user.ts";
import { base } from "../../styles.ts";
import { customEvent } from "../../util/event.ts";
import "../dui/radio/dui-radio-group.ts";
import "../dui/radio/dui-radio.ts";
import "../dui/checkbox/dui-checkbox-group.ts";
import "../dui/checkbox/dui-checkbox.ts";
import "../dui/input/dui-input.ts";
import "../dui/button/dui-button.ts";

type Option = string | CardOption;

type ChoiceQuestion = {
  question: string;
  type: "single_select" | "multi_select";
  options: Option[];
  allow_freeform: boolean;
};

const isRichOption = (opt: Option): opt is CardOption =>
  typeof opt === "object" && "label" in opt;

const optionValue = (opt: Option): string =>
  isRichOption(opt) ? opt.label : opt;

const OTHER_VALUE = "__other__";

export const submitAnswersEvent = customEvent<{ value: string }>(
  "submit-answers",
  { bubbles: true, composed: true },
);

const styles = css`
  :host {
    display: block;
  }

  :host([hidden]) {
    display: none;
  }

  .questions {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    padding: var(--space-5);
    border: var(--border-width-thin) solid var(--muted-foreground);
    border-radius: var(--radius-md);
    margin: var(--extra-margin-for-bordered-messages) 0;
  }

  .question-label {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-medium);
    margin: 0 0 var(--space-3) 0;
  }

  .freeform-input {
    margin-top: var(--space-2);
  }

  .submit-row {
    display: flex;
    justify-content: flex-end;
  }

  .read-only-answers {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .read-only-answer {
    font-size: var(--font-size-base);
    color: var(--muted-foreground);
    line-height: var(--line-height-normal);
  }

  .read-only-answer strong {
    color: var(--foreground);
  }

  .read-only-description {
    font-size: var(--font-size-base);
    color: var(--muted-foreground);
    line-height: var(--line-height-normal);
  }

  /* Card styles */
  .cards {
    display: flex;
    flex-direction: column;
  }

  dui-radio::part(root) {
    align-self: start;
    margin-top: 0.2em;
  }

  dui-radio .radio-label,
  dui-checkbox .checkbox-label {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-regular);
    line-height: var(--line-height-snug);
  }

  dui-radio.card,
  dui-checkbox.card {
    padding: var(--space-5) 0;
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: border-color var(--duration-fast) var(--ease-2);
    font-size: var(--font-size-base);
    line-height: var(--line-height-snug);
    gap: var(--space-3);
    position: relative;
  }

  dui-radio.card:hover,
  dui-checkbox.card:hover {
    border-color: var(--muted-foreground);
  }

  dui-radio.card[data-selected],
  dui-checkbox.card[data-selected] {
    border-color: var(--primary);
  }

  dui-radio.card[disabled],
  dui-checkbox.card[disabled] {
    pointer-events: none;
    opacity: 0.6;
  }

  .card::before,
  .card:last-child::after {
    content: "";
    position: absolute;
    width: 100%;
    height: var(--border-width-thin);
    background-color: var(--border);
    top: 0;
    left: 0;
  }

  .card:last-child::after {
    top: initial;
    bottom: 0;
  }

  .card-body {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    min-width: 0;
  }

  .card-label {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-medium);
    line-height: var(--line-height-snug);
  }

  .card-description {
    font-size: var(--font-size-sm);
    color: var(--muted-foreground);
    line-height: var(--line-height-snug);
  }
`;

@customElement("tool-ask-user")
export class ToolChoicesViewElement extends LitElement {
  static override styles = [base, styles];

  @property({ attribute: false })
  accessor questions: ChoiceQuestion[] = [];

  @property({ type: String })
  accessor display: "list" | "card" = "list";

  @property({ type: Boolean })
  accessor disabled = false;

  @state()
  accessor #answers: Map<number, string | string[]> = new Map();

  @state()
  accessor #freeformValues: Map<number, string> = new Map();

  @state()
  accessor #submitted = false;

  get #isDisabled(): boolean {
    return this.disabled || this.#submitted;
  }

  #isFreeformActive(index: number): boolean {
    const answer = this.#answers.get(index);
    if (Array.isArray(answer)) return answer.includes(OTHER_VALUE);
    return answer === OTHER_VALUE;
  }

  get #canSubmit(): boolean {
    return this.questions.every((_, i) => {
      const answer = this.#answers.get(i);
      if (answer === undefined) return false;
      if (this.#isFreeformActive(i)) {
        const freeform = this.#freeformValues.get(i);
        return !!freeform?.trim();
      }
      if (Array.isArray(answer)) return answer.length > 0;
      return true;
    });
  }

  #onRadioChange(index: number, e: CustomEvent<{ value: string }>): void {
    e.stopPropagation();
    this.#answers.set(index, e.detail.value);
    this.requestUpdate();
  }

  #onCheckboxChange(index: number, e: CustomEvent<string[]>): void {
    e.stopPropagation();
    this.#answers.set(index, e.detail);
    this.requestUpdate();
  }

  #onFreeformChange(index: number, e: CustomEvent<{ value: string }>): void {
    e.stopPropagation();
    this.#freeformValues.set(index, e.detail.value);
    this.requestUpdate();
  }

  #formatAnswers(): string {
    return this.questions
      .map((q, i) => {
        const raw = this.#answers.get(i);

        if (q.type === "single_select") {
          const answer =
            raw === OTHER_VALUE
              ? (this.#freeformValues.get(i) ?? "")
              : (raw as string);
          return `Q: ${q.question} → ${answer}`;
        }

        const arr = Array.isArray(raw) ? raw : [raw as string];
        const selected = arr.filter((v) => v !== OTHER_VALUE);
        if (this.#isFreeformActive(i)) {
          const freeform = this.#freeformValues.get(i) ?? "";
          if (freeform.trim()) selected.push(freeform.trim());
        }
        return `Q: ${q.question} → ${selected.join(", ")}`;
      })
      .join("\n");
  }

  #onSubmit(): void {
    if (!this.#canSubmit) return;
    this.#submitted = true;
    this.dispatchEvent(submitAnswersEvent({ value: this.#formatAnswers() }));
  }

  #renderCardBody(opt: Option): TemplateResult {
    const value = optionValue(opt);
    const rich = isRichOption(opt) ? opt : undefined;
    return html`
      <div class="card-body">
        <div class="card-label">${value}</div>
        ${rich
          ? html`<div class="card-description">${rich.description}</div>`
          : nothing}
      </div>
    `;
  }

  #renderCardQuestion(q: ChoiceQuestion, index: number): TemplateResult {
    const currentAnswer = this.#answers.get(index);
    const isMulti = q.type === "multi_select";
    const freeformActive = this.#isFreeformActive(index);

    const isSelected = (value: string): boolean =>
      isMulti
        ? Array.isArray(currentAnswer) && currentAnswer.includes(value)
        : currentAnswer === value;

    const freeformInput = html`
      <dui-input
        class="freeform-input"
        placeholder="Type your answer..."
        ?disabled="${this.#isDisabled}"
        @input-change="${(e: CustomEvent<{ value: string }>) =>
          this.#onFreeformChange(index, e)}"
      ></dui-input>
    `;

    if (isMulti) {
      return html`
        <div class="question">
          <p class="question-label">${q.question}</p>
          <dui-checkbox-group
            ?disabled="${this.#isDisabled}"
            @value-change="${(e: CustomEvent<string[]>) =>
              this.#onCheckboxChange(index, e)}"
          >
            <div class="cards">
              ${q.options.map((opt) => {
                const value = optionValue(opt);
                return html`
                  <dui-checkbox
                    value="${value}"
                    class="card"
                    ?data-selected="${isSelected(value)}"
                  >
                    ${this.#renderCardBody(opt)}
                  </dui-checkbox>
                `;
              })}
              ${q.allow_freeform
                ? html`
                    <dui-checkbox
                      value="${OTHER_VALUE}"
                      class="card"
                      ?data-selected="${freeformActive}"
                    >
                      <div class="card-body">
                        <div class="card-label">Other</div>
                        <div class="card-description">
                          Suggest your own alternative
                        </div>
                      </div>
                    </dui-checkbox>
                  `
                : nothing}
            </div>
          </dui-checkbox-group>
          ${freeformActive ? freeformInput : nothing}
        </div>
      `;
    }

    return html`
      <div class="question">
        <p class="question-label">${q.question}</p>
        <dui-radio-group
          ?disabled="${this.#isDisabled}"
          @value-change="${(e: CustomEvent<{ value: string }>) =>
            this.#onRadioChange(index, e)}"
        >
          <div class="cards">
            ${q.options.map((opt) => {
              const value = optionValue(opt);
              return html`
                <dui-radio
                  value="${value}"
                  class="card"
                  ?data-selected="${isSelected(value)}"
                >
                  ${this.#renderCardBody(opt)}
                </dui-radio>
              `;
            })}
            ${q.allow_freeform
              ? html`
                  <dui-radio
                    value="${OTHER_VALUE}"
                    class="card"
                    ?data-selected="${freeformActive}"
                  >
                    <div class="card-body">
                      <div class="card-label">Other</div>
                      <div class="card-description">
                        Suggest your own alternative
                      </div>
                    </div>
                  </dui-radio>
                `
              : nothing}
          </div>
        </dui-radio-group>
        ${freeformActive ? freeformInput : nothing}
      </div>
    `;
  }

  #renderQuestion(q: ChoiceQuestion, index: number): TemplateResult {
    if (this.display === "card") {
      return this.#renderCardQuestion(q, index);
    }

    const freeformActive = this.#isFreeformActive(index);

    if (q.type === "single_select") {
      return html`
        <div class="question">
          <p class="question-label">${q.question}</p>
          <dui-radio-group
            ?disabled="${this.#isDisabled}"
            @value-change="${(e: CustomEvent<{ value: string }>) =>
              this.#onRadioChange(index, e)}"
          >
            ${q.options.map(
              (opt) =>
                html`<dui-radio value="${optionValue(opt)}"
                  ><span class="radio-label"
                    >${optionValue(opt)}</span
                  ></dui-radio
                >`,
            )}
            ${q.allow_freeform
              ? html`<dui-radio value="${OTHER_VALUE}"
                  ><span class="radio-label">Other...</span></dui-radio
                >`
              : nothing}
          </dui-radio-group>
          ${freeformActive
            ? html`
                <dui-input
                  class="freeform-input"
                  placeholder="Type your answer..."
                  ?disabled="${this.#isDisabled}"
                  @input-change="${(e: CustomEvent<{ value: string }>) =>
                    this.#onFreeformChange(index, e)}"
                ></dui-input>
              `
            : nothing}
        </div>
      `;
    }

    return html`
      <div class="question">
        <p class="question-label">${q.question}</p>
        <dui-checkbox-group
          ?disabled="${this.#isDisabled}"
          @value-change="${(e: CustomEvent<string[]>) =>
            this.#onCheckboxChange(index, e)}"
        >
          ${q.options.map(
            (opt) =>
              html`<dui-checkbox value="${optionValue(opt)}"
                ><span class="checkbox-label"
                  >${optionValue(opt)}</span
                ></dui-checkbox
              >`,
          )}
          ${q.allow_freeform
            ? html`<dui-checkbox value="${OTHER_VALUE}"
                ><span class="checkbox-label">Other...</span></dui-checkbox
              >`
            : nothing}
        </dui-checkbox-group>
        ${freeformActive
          ? html`
              <dui-input
                class="freeform-input"
                placeholder="Type your answer..."
                ?disabled="${this.#isDisabled}"
                @input-change="${(e: CustomEvent<{ value: string }>) =>
                  this.#onFreeformChange(index, e)}"
              ></dui-input>
            `
          : nothing}
      </div>
    `;
  }

  #renderReadOnlyAnswers(): TemplateResult {
    return html`
      <div class="read-only-answers">
        ${this.questions.map((q, i) => {
          const raw = this.#answers.get(i);
          let answer: string;

          if (q.type === "single_select") {
            answer =
              raw === OTHER_VALUE
                ? (this.#freeformValues.get(i) ?? "")
                : (raw as string);
          } else {
            const arr = Array.isArray(raw) ? raw : [raw as string];
            const selected = arr.filter((v) => v !== OTHER_VALUE);
            if (this.#isFreeformActive(i)) {
              const freeform = this.#freeformValues.get(i) ?? "";
              if (freeform.trim()) selected.push(freeform.trim());
            }
            answer = selected.join(", ");
          }

          // Find descriptions for rich options (works for both single and multi)
          const selectedValues = Array.isArray(raw)
            ? raw.filter((v) => v !== OTHER_VALUE)
            : raw !== OTHER_VALUE
              ? [raw as string]
              : [];
          const descriptions = q.options
            .filter(
              (opt) =>
                isRichOption(opt) && selectedValues.includes(optionValue(opt)),
            )
            .map((opt) => (opt as CardOption).description);
          const description =
            descriptions.length > 0 ? descriptions.join("; ") : undefined;

          return html`
            <div class="read-only-answer">
              <strong>${q.question}</strong> → ${answer}
              ${description
                ? html`<div class="read-only-description">${description}</div>`
                : nothing}
            </div>
          `;
        })}
      </div>
    `;
  }

  protected override willUpdate(): void {
    this.hidden = this.#submitted || this.disabled;
  }

  override render(): TemplateResult | typeof nothing {
    if (this.#submitted || this.disabled) {
      return nothing;
    }

    return html`
      <div class="questions">
        ${this.questions.map((q, i) => this.#renderQuestion(q, i))}
        ${!this.#isDisabled
          ? html`
              <div class="submit-row">
                <dui-button
                  variant="primary"
                  ?disabled="${!this.#canSubmit}"
                  @click="${this.#onSubmit}"
                  >Continue</dui-button
                >
              </div>
            `
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "tool-ask-user": ToolChoicesViewElement;
  }
}
